// Single row in the Bibliography tab: title/author/year, source-type icon,
// and remove action. Per-Piece cited/consulted toggle was removed 2026-07-05
// because the render pipeline emits a single "Bibliography" section that
// includes every attached reference (see mem://design/bibliography-single-section);
// the toggle had no user-visible effect. The `cited` column on
// content_piece_references is preserved in the DB in case a future
// "further reading" split is added.
import { Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { getSourceTypeIcon } from "@/lib/sourceTypeIcons";
import type { BibliographyEntry } from "./useBibliographyEntries";

const fallbackAuthor = (entry: BibliographyEntry): string | null => {
  const item = entry.item;
  if (!item) return null;
  if (Array.isArray(item.authors) && item.authors.length > 0) return String(item.authors[0]);
  return item.author_text ?? null;
};

type Props = {
  entry: BibliographyEntry;
  onRemove: () => void;
};

export const BibliographyReferenceRow = ({ entry, onRemove }: Props) => {
  const item = entry.item;
  const title = item?.title ?? entry.labelSnapshot ?? "(unavailable library item)";
  const author = fallbackAuthor(entry);
  const year = item?.published_year ?? null;
  const sourceType = item?.source_type ?? null;

  const SourceIcon = sourceType ? getSourceTypeIcon(sourceType) : null;

  return (
    <li className="rounded-md border border-border bg-background p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-sm font-medium leading-snug text-foreground">{title}</p>
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            {SourceIcon ? <SourceIcon className="h-3 w-3 shrink-0" /> : null}
            <span>
              {author ? author : <span className="italic">Unknown author</span>}
              {year ? ` · ${year}` : ""}
            </span>
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                onClick={onRemove}
                aria-label="Remove from this piece"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Remove from this piece</TooltipContent>
          </Tooltip>
        </div>
      </div>
    </li>
  );
};
